/**
 * Init + apply-copied effects for Edit Sent form.
 */

import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { toClickIdStructure, toDomainScheme } from '@trafficpulse/shared';

import { filterSenderNamesMax11 } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import type { BulkUpdatePreparedDropsPayload } from '@/hooks';
import type {
  ClickIdStructureValue,
  DomainSchemeValue,
} from '@/features/smswiser-campaigns/utils/shortlinkOptionState';

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

const LEAVE_TLD_UNCHANGED = '__unchanged__';

interface BootstrapArgs {
  drop: PreparedDrop | null;
  isBulk: boolean;
  initialApplyPayload: BulkUpdatePreparedDropsPayload | null | undefined;
  onInitialApplyConsumed: (() => void) | undefined;
  setRouteId: Dispatch<SetStateAction<number | ''>>;
  setTldValue: Dispatch<SetStateAction<string>>;
  setTitle: Dispatch<SetStateAction<string>>;
  setSelectedSenderNames: Dispatch<SetStateAction<string[]>>;
  setSelectedSmsTexts: Dispatch<SetStateAction<string[]>>;
  setForceSend: Dispatch<SetStateAction<boolean | 'unchanged'>>;
  setUseOnlyNewDomains: Dispatch<SetStateAction<boolean | 'unchanged'>>;
  setClickIdStructure: Dispatch<SetStateAction<ClickIdStructureValue>>;
  setDomainScheme: Dispatch<SetStateAction<DomainSchemeValue>>;
}

export function useEditSentDropBootstrap(a: BootstrapArgs): void {
  const {
    drop,
    isBulk,
    initialApplyPayload,
    onInitialApplyConsumed,
    setRouteId,
    setTldValue,
    setTitle,
    setSelectedSenderNames,
    setSelectedSmsTexts,
    setForceSend,
    setUseOnlyNewDomains,
    setClickIdStructure,
    setDomainScheme,
  } = a;

  useEffect(() => {
    if (!drop) return;
    if (isBulk) {
      setRouteId('');
      setTldValue(LEAVE_TLD_UNCHANGED);
      setSelectedSenderNames([]);
      setSelectedSmsTexts([]);
      setForceSend('unchanged');
      setUseOnlyNewDomains('unchanged');
      setClickIdStructure('unchanged');
      setDomainScheme('unchanged');
    } else {
      setTitle(drop.sentTitle ?? drop.titleBase ?? '');
      setRouteId(drop.routeId);
      setTldValue(drop.shortLinkTldValue ?? '');
      setSelectedSenderNames(Array.from(new Set(drop.senderNames ?? [])));
      setSelectedSmsTexts(Array.from(new Set(drop.smsTexts ?? [])));
      setForceSend(drop.forceSend ?? false);
      setUseOnlyNewDomains(drop.useOnlyNewDomains ?? false);
      setClickIdStructure(toClickIdStructure(drop.clickIdStructure));
      setDomainScheme(toDomainScheme(drop.domainScheme));
    }
  }, [drop, isBulk, setRouteId, setTldValue, setTitle, setSelectedSenderNames, setSelectedSmsTexts, setForceSend, setUseOnlyNewDomains, setClickIdStructure, setDomainScheme]);

  useEffect(() => {
    const p = initialApplyPayload;
    if (!p) return;
    if (p.routeId != null && p.routeId > 0) setRouteId(p.routeId);
    if (p.senderNames?.length) {
      setSelectedSenderNames(filterSenderNamesMax11(p.senderNames).slice(0, 50));
    }
    if (p.smsTexts?.length) {
      setSelectedSmsTexts(p.smsTexts.slice(0, 50));
    }
    onInitialApplyConsumed?.();
  }, [initialApplyPayload, onInitialApplyConsumed, setRouteId, setSelectedSenderNames, setSelectedSmsTexts]);
}
